import { useCallback, useEffect, useMemo, useState } from "react";

import { parseValue } from "../../../../../../ui library/helpers/functions";
import { moduleProps as modulePropOverrides } from "../../DynamicSection/helpers/propNameHelper";
import { orderFieldsForClientDemographics } from "../utils/layout";

const buildInitialForm = (schema, data) => {
  const isFlatSchema = schema?.type && !schema?.expected_input;
  return isFlatSchema ? { field: data } : { ...data };
};

export const useClientDemographicsForm = ({ schema, data, questionKey, handleAnswerChange }) => {
  const isFlatSchema = schema?.type && !schema?.expected_input;
  const [form, setForm] = useState(() => buildInitialForm(schema, data));

  useEffect(() => {
    setForm(buildInitialForm(schema, data));
  }, [schema, data]);

  const expectedInput = schema?.expected_input;
  const selectedSchema = isFlatSchema ? schema : schema?.[expectedInput];
  const properties = useMemo(
    () => (isFlatSchema ? { field: schema } : selectedSchema?.properties ?? {}),
    [isFlatSchema, schema, selectedSchema]
  );
  const requiredFields = useMemo(
    () => (isFlatSchema ? [] : selectedSchema?.required ?? []),
    [isFlatSchema, selectedSchema]
  );

  const fields = useMemo(
    () => Object.keys(properties || {}).filter((name) => name !== "id"),
    [properties]
  );
  const orderedFields = useMemo(
    () => orderFieldsForClientDemographics(fields),
    [fields]
  );

  const getPropDefinition = useCallback(
    (fieldName) => ({
      ...(properties?.[fieldName] ?? {}),
      ...(modulePropOverrides?.[fieldName] ?? {}),
    }),
    [properties]
  );

  const onChange = useCallback(
    (field, value) => {
      const fieldSchema = properties?.[field];
      const parsedValue = parseValue(value, fieldSchema?.type);
      setForm((prev) => {
        const updated = { ...prev, [field]: parsedValue };
        const resultToSend = isFlatSchema ? parsedValue : updated;
        handleAnswerChange(questionKey, resultToSend);
        return updated;
      });
    },
    [properties, handleAnswerChange, questionKey, isFlatSchema]
  );

  return {
    form,
    orderedFields,
    getPropDefinition,
    requiredFields,
    isFlatSchema,
    properties,
    onChange,
  };
};

export default useClientDemographicsForm;
